<template>
	<div class="proforma" v-if="customer && currency">
		<!-- HEADER DOCUMENTO -->
		<div class="proforma-header">
			<div class="proforma-title-block">
				<div class="proforma-title">{{ $t("PROFORMA INVOICE") }}</div>
				<div class="proforma-meta">
					<div><span class="text-bold">{{ $t("Document number") }}:</span> {{ invoice.external_invoice_id || ('#' + (invoice.id || '—')) }}</div>
					<div><span class="text-bold">{{ $t("Date") }}:</span> {{ formatDate(invoice.inserted_at) }}</div>
				</div>
			</div>
			<div class="proforma-status">
				<span class="badge inline" :class="invoice.invoiced ? 'success' : 'warning'">
					{{ invoice.invoiced ? $t("Invoiced") : $t("Not invoiced") }}
				</span>
			</div>
		</div>

		<!-- PARTI -->
		<div class="proforma-parties">
			<div class="party">
				<div class="party-label">{{ $t("Issued by") }}</div>
				<div class="party-name">{{ $root.authObj.company }}</div>
			</div>
			<div class="party">
				<div class="party-label">{{ $t("Bill to") }}</div>
				<div v-if="customer">
					<div class="party-name">{{ customer.company_name || "—" }}</div>
					<div v-if="customer.vat_number" class="party-line"><span class="text-bold">{{ $t("VAT") }}:</span> {{ customer.vat_number }}</div>
					<div v-if="customer.address || customer.city || customer.postal_code" class="party-line">
						{{ [customer.address, [customer.postal_code, customer.city, customer.province].filter(Boolean).join(' '), customer.country].filter(Boolean).join(', ') }}
					</div>
					<div v-if="customer.pec_email" class="party-line"><span class="text-bold">PEC:</span> {{ customer.pec_email }}</div>
					<div v-if="customer.sdi_code" class="party-line"><span class="text-bold">SDI:</span> {{ customer.sdi_code }}</div>
				</div>
				<div v-else class="text-small fg-dark">
					<span class="mif-spinner2 ani-spin mr-1"></span>{{ $t("Loading customer...") }}
				</div>
			</div>
		</div>

		<!-- DETTAGLIO -->
		<div class="proforma-detail">
			<table class="detail-table">
				<thead>
					<tr>
						<th class="col-num">#</th>
						<th class="col-desc">{{ $t("Ticket") }}</th>
						<th class="col-qty text-right">{{ $t("Hours") }}</th>
						<th class="col-rate text-right">{{ $t("Unit price") }}</th>
						<th class="col-amount text-right">{{ $t("Amount") }}</th>
					</tr>
				</thead>
				<tbody v-if="invoice.tickets && invoice.tickets.length > 0">
					<template v-for="(t, i) in invoice.tickets">
						<tr class="detail-row">
							<td class="col-num">
								{{ i + 1 }}
							</td>
							<td class="col-desc">
								<div class="text-bold">{{ t.title || "—" }}</div>
								<div v-if="t.description" class="text-small fg-dark" style="white-space:pre-wrap;">{{ t.description || "—" }}</div>
							</td>
							<td class="col-qty text-right">{{ formatDecimalHours(ticketDuration(t)) }}</td>
							<td class="col-rate text-right">{{ formatMoney(customer.hourly_rate, currency) }}</td>
							<td class="col-amount text-right text-bold">{{ formatMoney(ticketAmount(t), currency) }}</td>
						</tr>
						<tr class="sessions-row">
							<td></td>
							<td colspan="4">
								<div class="text-small text-bold mb-1">{{ $t("Sessions") }} ({{ (t.sessions || []).length }})</div>
								<div v-if="!t.sessions || t.sessions.length == 0" class="text-small fg-dark">
									{{ $t("No sessions for this ticket.") }}
								</div>
								<table v-else class="sessions-table">
									<thead>
										<tr>
											<th>{{ $t("Computer") }}</th>
											<th>{{ $t("Start") }}</th>
											<th>{{ $t("End") }}</th>
											<th class="text-right">{{ $t("Duration") }}</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="(s, i) in t.sessions" :key="s.id + ' ' + i">
											<td>{{ s.name_computer_host || "—" }}</td>
											<td>{{ formatUtcDate(s.dt_start) || "—" }}</td>
											<td>{{ formatUtcDate(s.dt_end) || "—" }}</td>
											<td class="text-right">{{ formatHours(s.total_time) }}</td>
										</tr>
									</tbody>
								</table>
							</td>
						</tr>
					</template>
				</tbody>
				<tbody v-else>
					<tr>
						<td colspan="5" class="text-center text-small fg-dark py-4">{{ $t("No tickets for this invoice.") }}</td>
					</tr>
				</tbody>
			</table>
		</div>

		<!-- TOTALI -->
		<div class="proforma-totals">
			<div class="totals-box">
				<div class="totals-row">
					<span class="totals-label">{{ $t("Subtotal") }}:</span>
					<span class="totals-value">{{ formatMoney(subtotal, currency) }}</span>
				</div>
				<div class="totals-row">
					<span class="totals-label">{{ $t("Tax") }}:</span>
					<span class="totals-value">—</span>
				</div>
				<div class="totals-row totals-row-total">
					<span class="totals-label">{{ $t("Total") }}:</span>
					<span class="totals-value">{{ formatMoney(subtotal, currency) }}</span>
				</div>
			</div>
		</div>

		<!-- NOTE -->
		<div v-if="invoice.note" class="proforma-notes">
			<div class="text-bold mb-1">{{ $t("Notes") }}</div>
			<div style="white-space:pre-wrap;">{{ invoice.note }}</div>
		</div>

		<!-- FOOTER DISCLAIMER -->
		<div class="proforma-footer text-small fg-dark text-center">
			{{ $t("This document is not a fiscal invoice.") }}
		</div>
	</div>
</template>

<script>
import moment from 'moment';
import { populateUsefulMaps } from '../../../utils/entityMethods/ticket';
import { formatMoney } from '../../../utils/entityMethods/customer';

export default {
	name: "InvoiceTemplate",
	props: {
		invoice: { type: Object, required: true },
		vatRate: { type: Number, default: 22 }
	},
	data() {
		return {
			customer: null,
			currency: null,
			expandedTickets: {},
			subtotal: 0
		};
	},
	async created() {
		await this.populateUsefulMaps();
		this.customer = this.$root.customerMap[this.invoice.id_customer];
		// La valuta è quella dell'invoice; fallback al customer e infine EUR.
		this.currency = this.invoice.currency || (this.customer && this.customer.currency) || "EUR";
		if (this.invoice.total_amount != null) {
			this.subtotal = Number(this.invoice.total_amount);
		}
		else if (!this.invoice.tickets) {
			this.subtotal = 0;
		}
		else { this.subtotal = this.invoice.tickets.reduce((sum, t) => sum + this.ticketAmount(t), 0); }
	},

	methods: {
		populateUsefulMaps,
		formatDate(d) {
			if (!d) return "—";
			return moment.utc(d).local().format("DD/MM/YYYY");
		},
		// input in secondi
		formatHours(seconds) {
			if (seconds == null) return "—";
			const s = Math.floor(Number(seconds));
			if (s === 0) return "0s";
			const h = Math.floor(s / 3600);
			const m = Math.floor((s % 3600) / 60);
			const sec = s % 60;
			if (h > 0) return h + "h" + (m > 0 ? " " + m + "m" : "");
			if (m > 0) return m + "m" + (sec > 0 ? " " + sec + "s" : "");
			return sec + "s";
		},
		// ore in decimale, max 2 cifre (es. "1.50")
		formatDecimalHours(seconds) {
			if (seconds == null) return "0.00";
			return (Number(seconds) / 3600).toFixed(2);
		},
		// Converte un importo (double) in stringa di prezzo rispettando this.currency:
		// separatori e simbolo di valuta sono scelti da Intl in base a locale + codice ISO 4217.
		formatMoney,
		ticketDuration(ticket) {
			if (!ticket || !Array.isArray(ticket.sessions)) return 0;
			return ticket.sessions.reduce((sum, s) => sum + (Number(s.total_time) || 0), 0);
		},
		ticketAmount(ticket) {
			const hours = this.ticketDuration(ticket) / 3600;
			return Number(this.customer.hourly_rate) * hours;
		},
		formatSessionNotes(notes) {
			if (!Array.isArray(notes) || notes.length == 0) return "—";
			return notes.map(n => (typeof n === "string" ? n : n.note)).filter(Boolean).join(" · ");
		},
		toggleTicket(id) {
			this.$set(this.expandedTickets, id, !this.expandedTickets[id]);
		},
		formatUtcDate(d) {
            if (!d) return "-";
            return moment.utc(d).local().format("DD/MM/YYYY HH:mm");
        },
	}
};
</script>

<style scoped>
.proforma {
	background: #fff;
	color: #222;
	font-family: "Helvetica Neue", Arial, sans-serif;
	padding: 28px 32px;
	border: 1px solid #d9d9d9;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.proforma-header {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	border-bottom: 2px solid #222;
	padding-bottom: 12px;
	margin-bottom: 20px;
}

.proforma-title {
	font-size: 26px;
	font-weight: 700;
	letter-spacing: 2px;
	color: #222;
}

.proforma-meta {
	margin-top: 8px;
	font-size: 13px;
	line-height: 1.6;
}

.proforma-parties {
	display: flex;
	gap: 24px;
	margin-bottom: 24px;
}

.party {
	flex: 1;
	background: #fafbfc;
	border: 1px solid #e2e8f0;
	padding: 12px 14px;
}

.party-label {
	font-size: 11px;
	text-transform: uppercase;
	letter-spacing: 1px;
	color: #888;
	margin-bottom: 6px;
}

.party-name {
	font-size: 15px;
	font-weight: 700;
	margin-bottom: 4px;
}

.party-line {
	font-size: 12px;
	color: #444;
	line-height: 1.4;
}

.proforma-detail {
	margin-bottom: 20px;
}

.detail-table {
	display: table !important;
	width: 100%;
	border-collapse: collapse;
	font-size: 13px;
	table-layout: auto;
}

.detail-table thead {
	display: table-header-group !important;
}

.detail-table tbody {
	display: table-row-group !important;
}

.detail-table tr {
	display: table-row !important;
}

.detail-table th,
.detail-table td {
	display: table-cell !important;
}

.detail-table thead th {
	background: grey;
	color: #fff;
	padding: 8px 10px;
	font-weight: 600;
	text-align: left;
}

.detail-table thead th.text-right {
	text-align: right;
}

.detail-table tbody td {
	padding: 10px;
	border-bottom: 1px solid #e2e8f0;
	vertical-align: top;
}

.col-num {
	width: 44px;
}

.col-qty,
.col-rate,
.col-amount {
	width: 110px;
	white-space: nowrap;
}

.detail-row {
	cursor: pointer;
	transition: background 0.1s;
}

.detail-row:hover {
	background: #f6f7f8;
}

.row-toggle {
	color: #555;
}

.sessions-row td {
	background: #f8f9fa;
	padding: 10px 14px !important;
}

.sessions-table {
	display: table !important;
	width: 100%;
	border-collapse: collapse;
	font-size: 12px;
	background: #fff;
	border: 1px solid #e2e8f0;
}

.sessions-table thead {
	display: table-header-group !important;
}

.sessions-table tbody {
	display: table-row-group !important;
}

.sessions-table tr {
	display: table-row !important;
}

.sessions-table th,
.sessions-table td {
	display: table-cell !important;
}

.sessions-table thead th {
	background: #eef0f2;
	color: #444;
	padding: 6px 10px;
	text-align: left;
}

.sessions-table tbody td {
	padding: 6px 10px;
	border-bottom: 1px solid #f0f1f2;
}

.session-notes {
	max-width: 260px;
	white-space: normal;
	word-break: break-word;
}

.proforma-totals {
	display: flex;
	justify-content: flex-end;
	margin-bottom: 20px;
}

.totals-box {
	min-width: 280px;
}

.totals-row {
	display: flex;
	justify-content: space-between;
	padding: 6px 0;
	font-size: 13px;
}

.totals-row-total {
	border-top: 2px solid #222;
	border-bottom: 2px solid #222;
	font-size: 16px;
	font-weight: 700;
	margin-top: 4px;
}

.totals-label {
	color: #555;
}

.totals-row-total .totals-label,
.totals-row-total .totals-value {
	color: #222;
}

.proforma-notes {
	background: #fffbe6;
	border: 1px solid #f3e8a8;
	padding: 10px 14px;
	margin-bottom: 16px;
	font-size: 13px;
}

.proforma-footer {
	border-top: 1px solid #e2e8f0;
	padding-top: 10px;
	font-style: italic;
}
</style>
